import { Alert, Button, Snackbar, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector } from "../../app/hooks";
import {
    fetchMessagesUnreadCount,
    sendMessagesPresenceHeartbeat,
    type MessageUnreadPreview,
} from "../../lib/api/messages";
import { formatMessageDateTime } from "../../lib/presentation";
import { routePaths } from "../../routes/navigation";

function previewHeadline(item: MessageUnreadPreview) {
    if (item.conversationType === "direct") {
        return item.senderName || item.conversationTitle;
    }

    return item.conversationTitle;
}

export default function MessagesNotificationHost() {
    const user = useAppSelector((state) => state.auth.user);
    const location = useLocation();
    const navigate = useNavigate();
    const onMessagesPage = location.pathname === routePaths.messages;
    const initializedRef = useRef(false);
    const lastTopMessageIdRef = useRef<number | null>(null);
    const [activePreview, setActivePreview] = useState<MessageUnreadPreview | null>(null);
    const [extraCount, setExtraCount] = useState(0);
    const [open, setOpen] = useState(false);

    const unreadQuery = useQuery({
        queryKey: ["messages-unread-count"],
        queryFn: fetchMessagesUnreadCount,
        enabled: Boolean(user),
        refetchInterval: 30_000,
        refetchIntervalInBackground: true,
        retry: 1,
    });

    useQuery({
        queryKey: ["messages-presence-heartbeat"],
        queryFn: sendMessagesPresenceHeartbeat,
        enabled: Boolean(user),
        refetchInterval: 20_000,
        refetchIntervalInBackground: true,
        retry: 1,
        staleTime: 0,
    });

    useEffect(() => {
        const preview = unreadQuery.data?.preview ?? [];

        if (preview.length === 0) {
            if (initializedRef.current) {
                lastTopMessageIdRef.current = null;
            }

            return;
        }

        const topMessageId = preview[0]?.messageId ?? null;

        if (!initializedRef.current) {
            initializedRef.current = true;
            lastTopMessageIdRef.current = topMessageId;
            return;
        }

        if (topMessageId === null || topMessageId === lastTopMessageIdRef.current) {
            return;
        }

        const previousTopId = lastTopMessageIdRef.current;
        const freshItems = preview.filter(
            (item) => previousTopId === null || item.messageId > previousTopId,
        );

        lastTopMessageIdRef.current = topMessageId;

        if (freshItems.length === 0 || onMessagesPage) {
            return;
        }

        setActivePreview(freshItems[0]);
        setExtraCount(Math.max(0, freshItems.length - 1));
        setOpen(true);
    }, [onMessagesPage, unreadQuery.data]);

    const handleOpenMessages = () => {
        setOpen(false);

        const conversationId = activePreview?.conversationId;

        if (conversationId) {
            navigate(`${routePaths.messages}?conversation=${conversationId}`);
            return;
        }

        navigate(routePaths.messages);
    };

    if (!user) {
        return null;
    }

    return (
        <Snackbar
            open={open && activePreview !== null}
            autoHideDuration={12_000}
            onClose={(_, reason) => {
                if (reason === "clickaway") {
                    return;
                }

                setOpen(false);
            }}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Alert
                severity="info"
                variant="filled"
                sx={{ width: "100%", maxWidth: 420, alignItems: "flex-start" }}
                action={(
                    <Button color="inherit" size="small" onClick={handleOpenMessages}>
                        Отвори
                    </Button>
                )}
            >
                <Stack spacing={0.5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Ново съобщение
                        {extraCount > 0 ? ` (+${extraCount})` : ""}
                    </Typography>
                    {activePreview ? (
                        <>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {previewHeadline(activePreview)}
                            </Typography>
                            <Typography variant="body2">
                                {activePreview.body || "—"}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                {formatMessageDateTime(activePreview.createdAt)}
                            </Typography>
                        </>
                    ) : null}
                </Stack>
            </Alert>
        </Snackbar>
    );
}
